/**
 * Issue #211 (ratings half) — the pure rules for visitor ratings of playing
 * surfaces, kept out of the components for `lib/facilities.js`'s reason:
 * a rule in a component is a rule the second surface forgets.
 *
 * VOCABULARY GUARD: these are VISITOR ratings ("what the people who played
 * here found"), a different fact from `condition` ("what the record-keeper
 * says it is") — #211's 2026-08-21 status check draws the line. Nothing in
 * this module reads or writes `condition`.
 *
 * THE WIRE (see `api/src/routes/facilityRatings.ts`, which owns the shape):
 * aggregates + count + the caller's OWN rating only. No rater identity of
 * any kind reaches this client, so nothing here can render one — that is
 * Q9(a)/#169 (junior protection) enforced upstream, not softened here.
 */

/**
 * Martin's axis set (#211, 2026-08-31 — supersedes #538's three), with his
 * scale anchors VERBATIM: "a 3 on an unanchored axis means whatever the
 * rater assumed it meant", so the anchors render beside each axis in the
 * editor, not only in this comment.
 *
 * The CONDITION trio runs bad→good and rolls up into the server's
 * `overall`. `turn_grip` is CHARACTER — a fast, true seamer's pitch that
 * does nothing for spin is a good pitch that scores 1 — so the server
 * never averages it into `overall` and this client renders it separately.
 */
export const CONDITION_AXES = [
  { key: "pace_bounce", label: "Pace & bounce", anchors: { 1: "slow/low, dead", 5: "fast, true bounce through the surface" } },
  { key: "surface_evenness", label: "Surface evenness", anchors: { 1: "cracked, footholds, patchy", 5: "smooth, even, well-covered" } },
  { key: "maintenance", label: "Maintenance", anchors: { 1: "neglected (overgrown, unrolled, unwatered)", 5: "well-kept, regularly rolled/mowed" } },
];

/** The character axis — turf-only (see `axesForSurface`), and optional even
 * there: a rater with no read on spin withholds rather than inventing a 3. */
export const TURN_GRIP_AXIS = {
  key: "turn_grip",
  label: "Turn & grip",
  anchors: { 1: "no assistance for spin", 5: "significant turn/grip" },
  optional: true,
};

export const RATING_AXES = [...CONDITION_AXES, TURN_GRIP_AXIS];

export const RATING_SCALE = [1, 2, 3, 4, 5];

/**
 * The axis set varies by what the thing is MADE OF (#211, 2026-08-31,
 * unvetoed): turf gets all four; on synthetic, matting and concrete, spin
 * assistance is a property of the material, so Turn & grip says nothing
 * about that individual net and the API refuses it (400).
 */
export function axesForSurface(surface) {
  return surface === "turf" ? RATING_AXES : CONDITION_AXES;
}

/** "1 = slow/low, dead · 5 = fast, true bounce through the surface" — the
 * anchor helper line the editor shows under each axis label. */
export function axisAnchorText(axis) {
  return `1 = ${axis.anchors[1]} · 5 = ${axis.anchors[5]}`;
}

/**
 * All CONDITION axes chosen — a partial verdict on those is not a verdict
 * (the PUT refuses one, so the Save button must too). `turn_grip` never
 * gates: it is optional on turf and absent elsewhere.
 */
export function ratingDraftComplete(draft) {
  return CONDITION_AXES.every(({ key }) => RATING_SCALE.includes(draft?.[key]));
}

/**
 * The PUT body for one surface's draft: exactly the axes that exist there.
 * `turn_grip` travels only on turf and only when a verdict was given —
 * omitting it withdraws any earlier turn verdict (PUT replaces), and a
 * non-turf body never carries the key at all (the API 400s it).
 */
export function ratingBodyFromDraft(draft, surface) {
  const body = {};
  for (const { key } of CONDITION_AXES) body[key] = draft?.[key];
  if (surface === "turf" && RATING_SCALE.includes(draft?.[TURN_GRIP_AXIS.key])) {
    body[TURN_GRIP_AXIS.key] = draft[TURN_GRIP_AXIS.key];
  }
  return body;
}

/**
 * COUNT-FORWARD, always: "4 ratings · avg 3.8", "1 rating", "No ratings
 * yet". The average appears only when the server sent one — it withholds
 * it below 2 ratings (sample-size honesty is SERVER policy; this function
 * merely cannot manufacture what it was not given). `overall` is the
 * CONDITION mean only — the server keeps Turn & grip out of it.
 */
export function ratingCountLabel(count, overall) {
  if (!count) return "No ratings yet";
  const noun = count === 1 ? "rating" : "ratings";
  return overall == null ? `${count} ${noun}` : `${count} ${noun} · avg ${overall}`;
}

/**
 * CHARACTER, said beside the quality figures and never inside them:
 * "Turn & grip 4.5 (2 rated)" — or, below the server's sample threshold,
 * just how many have scored it. Null when the season has no turn verdicts
 * (a non-turf surface, or nobody would judge spin).
 */
export function turnGripLabel(turnGrip) {
  if (!turnGrip || !turnGrip.count) return null;
  if (turnGrip.average == null) {
    return `Turn & grip: rated by ${turnGrip.count}`;
  }
  return `Turn & grip ${turnGrip.average} (${turnGrip.count} rated)`;
}

/**
 * The season group to LEAD with: the current season when it has ratings,
 * otherwise nothing — an old verdict is history, not a headline (#211's
 * resurfaced-net rule). `seasons` arrives newest-first from the API.
 */
export function headlineSeason(entry, currentSeason) {
  const seasons = entry?.seasons || [];
  return seasons.find((s) => s.season === currentSeason) || null;
}

/** Every non-current season group, for the small history line. */
export function priorSeasons(entry, currentSeason) {
  return (entry?.seasons || []).filter((s) => s.season !== currentSeason);
}

/** "2024/25: 2 ratings · avg 1.5" */
export function seasonHistoryLabel(seasonGroup) {
  return `${seasonGroup.season}: ${ratingCountLabel(seasonGroup.count, seasonGroup.overall)}`;
}

/** The caller's own rating as an editing draft (or an empty one) — the
 * turn verdict included, so an update does not silently drop it. */
export function draftFromOwnRating(myRating) {
  const draft = {};
  for (const { key } of RATING_AXES) draft[key] = myRating?.[key] ?? null;
  return draft;
}

/**
 * WHY THERE IS NO RATE BUTTON, said to the person reading. Shown to a
 * viewer the server said cannot rate (`can_rate: false`) — a control that
 * 403s is a trap, and silence reads as a bug. The sentence states the gate,
 * which doubles as the instruction for opening it.
 */
export const INELIGIBLE_COPY =
  "Ratings come from people who have played here — log a match or training session at this ground to add yours.";

/**
 * Issue #546 — surface-specific evidence, SAID rather than enforced. When
 * the caller's own logged matches/sessions name THIS facility
 * (`played_facility_ids` on the ratings read), the rating block may say so:
 * rated-the-surface-you-logged is first-class evidence. It never gates —
 * a sibling surface at a played venue stays ratable (`can_rate` is the
 * only permission), so this label appearing on one row forbids nothing on
 * the next.
 *
 * Vocabulary is #544's: the KIND in words where somebody has said what the
 * surface is, "surface" where nobody has (never "pitch", and never a
 * guess).
 */
export function playedEvidenceLabel(kind) {
  if (kind === "field") return "You've played on this field";
  if (kind === "net") return "You've trained in this net";
  return "You've played on this surface";
}

/**
 * Issue #581 — the ONE rating write-path URL, so the log-a-match and
 * training forms hit exactly the endpoint the grounds-map editor does
 * (`PUT /grounds/:groundId/facilities/:facilityId/rating`, an upsert). The
 * forms POST the match/session first, then PUT here with the id off the
 * saved row — a rating cast before the row exists fails the played-here
 * gate (`userHasPlayedAtGround`), so ordering is load-bearing on a new
 * entry and irrelevant on an edit. The body is always `ratingBodyFromDraft`
 * above, so turn_grip still never travels off turf.
 */
export function facilityRatingPath(groundId, facilityId) {
  return `/grounds/${groundId}/facilities/${facilityId}/rating`;
}
